// LoginForm.tsx
import React from "react";
import * as yup from "yup";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";

import { Button } from "../../Reusable/Button";
import { Input } from "../../Reusable/Input";
import { MessageIcon } from "../../../assets/Icons";
import { useDarkMode } from "../../../context/DarkModeContext";
import DataUpLogo from "../../../assets/images/DataUpLogoAuth.svg";
import { login } from "../../../features/auth/store/authSlice";
import { useAppSelector, useAppDispatch } from "../../../hooks/hooks";
import { LoginFormContainerProps, LoginFormProps, FormData } from "../types";

export const LoginFormContainer: React.FC<LoginFormContainerProps> = ({
  onSuccess,
  error,
  user,
  closeLoginModal,
}) => {
  return (
    <>
      <Logo />
      <Title />
      <div className="space-y-4">
        <LoginForm
          onSuccess={onSuccess}
          closeLoginModal={closeLoginModal}
        />
        <StatusMessages error={error} user={user} />
      </div>
    </>
  );
};

const Logo = () => (
  <div className="flex justify-center mb-4">
    <img
      src={DataUpLogo}
      alt="DatauplogoAuth"
      className="w-[135.55px] h-[32px]"
    />
  </div>
);

const Title = () => (
  <h2 className="text-2xl font-bold text-center mb-6 dark:text-white">
    Welcome back
  </h2>
);

const LoginForm = ({
  onSuccess,
  closeLoginModal,
}: LoginFormProps) => {
  const schema = yup
    .object({
      identifier: yup.string().required().label("Email/Username/Phone numebr"),
      password: yup.string().required().label("Password"),
    })
    .required();

  const {
    register,
    handleSubmit,
    reset,
  } = useForm<FormData>({ resolver: yupResolver(schema) });

  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { darkMode } = useDarkMode();
  const { loading, error } = useAppSelector((state) => state.auth);

  const onFormSubmit = async (data: FormData) => {
    await dispatch(login(data))
      .unwrap()
      .then(() => {
        reset();
        // First close the modal, then trigger the success callback
        closeLoginModal();
        // Call onSuccess after closing modal
        onSuccess();
        navigate("/dashboard");
      })
      .catch(() => {
        toast(error);
      });
  };

  return (
    <form onSubmit={handleSubmit(onFormSubmit)} className="space-y-4">
      <Input
        type="text"
        placeholder="Email address/Username/Phone Number"
        icon={<MessageIcon theme={darkMode ? "dark" : "light"} />}
        register={register}
        name="identifier"
        required
      />

      <div className="space-y-2">
        <Input
          type="password"
          placeholder="Password"
          isPassword
          register={register}
          name="password"
          required
        />
      </div>

      <Button
        type="submit"
        bgColor="bg-gradient-to-r from-[#027EC2] to-[#039EF2]"
        textColor="text-white"
        borderRadius="rounded-lg"
        className="w-full h-10 flex items-center justify-center"
        disabled={loading}
      >
        {loading ? (
          <svg
            className="animate-spin h-5 w-5 text-white"
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
          >
            <circle
              className="opacity-25"
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              strokeWidth="4"
            ></circle>
            <path
              className="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
            ></path>
          </svg>
        ) : (
          "Sign In"
        )}
      </Button>
    </form>
  );
};

const StatusMessages = ({
  error,
  user,
}: {
  error?: string | null;
  user?: any;
}) => (
  <>
    {error && <p className="text-red-500">{error}</p>}
    {user && <p className="text-green-500">Logged In!</p>}
  </>
);

export default LoginFormContainer;
